import { isIos, isSafari } from '@adornis/base/client/frontend-helpers/index.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { injectAttributesOf } from '@adornis/chemistry/directives/inject-attributes.js';
import { inputResetStyle as realInputResetStyle } from '@adornis/chemistry/elements/theming/input-token.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { FormField } from '@adornis/formfield/form-field.js';
import type { fromEventType, inputType, plainInputOutput } from '@adornis/forms/x-plain-input.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { ref } from 'lit/directives/ref.js';
import { DateTime } from 'luxon';
import { fromEvent, takeUntil } from 'rxjs';

@customElement('tr-plain-input')
export class TrPlainInput extends FormField<plainInputOutput> {
  override styles() {
    return [realInputResetStyle(this)];
  }

  @property({ reflect: true }) type: inputType = 'text';
  @state() focused = false;

  protected _input?: HTMLInputElement;
  protected _rawValue = '';
  protected _renderRaw = false;

  override render() {
    if (baseConfig.get('DEPLOYMENT') === 'development' && isSafari() && ['month', 'week'].includes(this.type)) {
      console.warn(
        `Using input type ${this.type} on safari. Safari does not support this input type, so it will break.`,
      );
    }
    // Need this to show the "search" button on ios keyboard
    // No! just type="search" is not enough for frickin' ios
    if (this.type === 'search')
      return html` <form action="." @submit="${(e: Event) => e.preventDefault()}">${this.input()}</form> `;
    return this.input();
  }

  private processNewValue(value?: string, from?: fromEventType) {
    value ??= this._input!.value;
    this._rawValue = this._input!.value;
    let adjustedValue: Maybe<plainInputOutput> = value;
    switch (this.type) {
      case 'number':
        adjustedValue = this._handleNumberInput(value, from);
        break;
      case 'iban':
        adjustedValue = this._handleIbanInput(value, from);
        break;
      case 'tel':
        adjustedValue = this._handleTelInput(value, from);
        break;
      case 'time':
        adjustedValue = this._handleTimeInput(value, from);
        break;
      case 'date':
        adjustedValue = this._handleDateInput(value, from);
        break;
      case 'datetime-local':
        adjustedValue = this._handleDateTimeLocalInput(value, from);
        break;
      case 'week':
        adjustedValue = this._handleWeekInput(value, from);
        break;
      case 'month':
        adjustedValue = this._handleMonthInput(value, from);
        break;
      default:
        adjustedValue = this._handleTextInput(value, from);
        break;
    }
    this.value.next(adjustedValue);
    return adjustedValue;
  }

  private _applyLimit(adjustedValue: Maybe<plainInputOutput>, value?: string) {
    value ??= this._input!.value;
    const limit = this.getAttribute('limit');
    // limit number of characters with limit attribute
    if (limit && !isNaN(+limit)) {
      adjustedValue = value.length > +limit ? this.value.value : this.type === 'number' ? adjustedValue : value;
    }
  }

  private _handleNumberInput(value?: string, from?: fromEventType) {
    value ??= this._input!.value;
    let adjustedValue: Maybe<plainInputOutput> = value;
    if (value === '') return null; // dont set a number if nothing is entered

    // different characters for dots: https://www.compart.com/en/unicode/U+002E
    const dots = [0x0000002e, 0x00002024, 0x0000fe52, 0x0000ff0e].map(charCode => String.fromCharCode(charCode));
    // no commas
    let rawString = value.replace(',', '.');
    // no special snowflake dots
    dots.forEach(dot => (rawString = rawString.replace(dot, '.')));
    // nothing but digits and dots
    rawString = rawString.replace(/[^0-9.,\-]/gi, '');
    // only keep the first dot
    while (rawString.indexOf('.') !== rawString.lastIndexOf('.')) rawString = rawString.replace(/(.+)\./, '$1');
    // dont want to format people's punctuation away
    const lastChar = rawString.charAt(rawString.length - 1);
    if (lastChar === '.' || lastChar === '-') {
      this._renderRaw = true;
      return null;
    }
    // need a number
    const rawNumber = +rawString;
    // apply step size
    const step = +(this.getAttribute('step') || 0);
    adjustedValue = !step ? rawNumber : Math.round(rawNumber / +step) * +step;

    const min = +(this.getAttribute('min') || -Infinity);
    const max = +(this.getAttribute('max') || Infinity);
    if (from === 'arrowKey' && adjustedValue > max) {
      this.dispatchEvent(new CustomEvent('hit-max'));
      adjustedValue = max;
    }
    if (from === 'arrowKey' && adjustedValue < min) {
      this.dispatchEvent(new CustomEvent('hit-min'));
      adjustedValue = min;
    }
    this._applyLimit(adjustedValue, value);
    // console.log('raw value', input.value, input.valueAsNumber, input.validity.badInput, this._lastKey);
    // if (isNaN(input.valueAsNumber)) {
    //   if (input.validity.badInput) return;
    //   return this.value.next(0);
    // }
    // adjustedValue = input.valueAsNumber;
    const pad = this._getNumericAttributeSafe('pad');
    if (pad && from !== 'blur') requestAnimationFrame(() => this.setCursor(pad));
    this._renderRaw = adjustedValue === 0 && (value.includes('.') || value.includes(','));
    return adjustedValue;
  }
  private _handleMonthInput(value?: string, from?: fromEventType) {
    value ??= this._input!.value;
    let adjustedValue: Maybe<plainInputOutput> = value;
    if (!value) return null;
    adjustedValue = DateTime.fromISO(value);
    return adjustedValue;
  }
  private _handleWeekInput(value?: string, from?: fromEventType) {
    value ??= this._input!.value;
    let adjustedValue: Maybe<plainInputOutput> = value;
    if (!value) return null;
    adjustedValue = DateTime.fromISO(value);
    return adjustedValue;
  }
  private _handleDateTimeLocalInput(value?: string, from?: fromEventType) {
    value ??= this._input!.value;
    let adjustedValue: Maybe<plainInputOutput> = value;
    if (!value) return null;
    adjustedValue = DateTime.fromISO(value);
    return adjustedValue;
  }
  private _handleDateInput(value?: string, from?: fromEventType) {
    value ??= this._input!.value;
    let adjustedValue: Maybe<plainInputOutput> = value;
    if (!value) return null;
    adjustedValue = DateTime.fromISO(value);
    return adjustedValue;
  }
  private _handleTimeInput(value?: string, from?: fromEventType) {
    value ??= this._input!.value;
    let adjustedValue: Maybe<plainInputOutput> = value;
    if (!value) return null;
    const step = this._getNumericAttributeSafe('step');
    adjustedValue = DateTime.fromFormat(value, step && step < 60 ? 'TT' : 'T');
    return adjustedValue;
  }
  private _handleTelInput(value?: string, from?: fromEventType) {
    value ??= this._input!.value;
    let adjustedValue: Maybe<plainInputOutput> = value;
    if (!value.length || /^[+]*[(]{0,1}[0-9]{0,4}[)]{0,1}[-\s\.\/0-9]*$/.test(value)) adjustedValue = value;
    else adjustedValue = this.value.value;
    return adjustedValue;
  }
  private _handleIbanInput(value?: string, from?: fromEventType) {
    value ??= this._input!.value;
    let adjustedValue: Maybe<plainInputOutput> = value;
    const oldValue = this.value.value;
    const lastCharacter = value.slice(-1);

    if (!value.length) adjustedValue = value;
    // handle first two letters of iban
    else if (value.length <= 2)
      if (/[A-Za-z]/.test(lastCharacter)) adjustedValue = value.toUpperCase();
      else adjustedValue = oldValue;
    // handle numbers with automatic spaces
    else if (value.length <= 27) {
      if (/[0-9 ]/.test(lastCharacter))
        if ((value.length - 5) % 5 === 0 && lastCharacter === ' ') adjustedValue = value;
        else if ((value.length - 5) % 5 === 0) adjustedValue = oldValue + ' ' + lastCharacter;
        else adjustedValue = value;
      else adjustedValue = oldValue;

      // cap iban length
    } else adjustedValue = oldValue;
    return adjustedValue;
  }
  private _handleTextInput(value?: string, from?: fromEventType) {
    value ??= this._input!.value;
    const adjustedValue: Maybe<number | string> = value;
    this._applyLimit(adjustedValue, value);
    return adjustedValue;
  }

  override connectedCallback() {
    super.connectedCallback();

    // click on padding should also focus
    fromEvent(this, 'click')
      .pipe(takeUntil(this.disconnected))
      .subscribe(() => this._input?.focus());
  }

  private input() {
    return html`
      <input
        @focus=${() => (this.focused = true)}
        @input=${(e: Event) => {
          this.processNewValue();
        }}
        @search=${(e: Event) => {
          this.processNewValue();
        }}
        @keydown=${(e: KeyboardEvent) => {
          if (this.type === 'number') {
            const step = this._getNumericAttributeSafe('step') ?? 1;
            if (e.key.length === 1 && !/[0-9.,\-]/.test(e.key)) e.preventDefault();
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              this.processNewValue(+(this.value.value ?? 0) + step + '', 'arrowKey');
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              this.processNewValue(+(this.value.value ?? 0) - step + '', 'arrowKey');
            }
          }
        }}
        @keyup=${(e: KeyboardEvent) => {
          if (e.key === 'Enter')
            return this.dispatchEvent(
              new CustomEvent('submit', {
                detail: { value: this.processNewValue() },
                composed: true,
                bubbles: true,
              }),
            );
          if (e.key === 'Escape') return this.dispatchEvent(new CustomEvent('escape'));
        }}
        @blur=${(e: Event) => {
          this.focused = false;
          this.processNewValue(undefined, 'blur');
          this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: this.value.value } }));
        }}
        @click=${(e: Event) => e.stopPropagation()}
        .value=${live(this._displayValue())}
        ?disabled=${this.disabled}
        type=${this.type === 'number' ? 'text' : this.type}
        inputmode=${ifDefined(this.type === 'number' ? 'decimal' : undefined)}
        ${injectAttributesOf(this, ['type', 'disabled'])}
        ${ref(input => (this._input = input as HTMLInputElement | undefined))}
      />
    `;
  }

  select() {
    // workaround for the fact that .select doesnt work on iOS
    // we're also setting type = tel instead of number in the case of iOS because setSelectionRange
    // doesn't support selection on number inputs
    if (isIos()) this._input?.setSelectionRange(0, 999);
    else this._input?.select();
  }

  setCursor(position: number) {
    this._input?.setSelectionRange(position, position);
  }

  getCursor() {
    return this._input ? this._input.selectionStart : null;
  }

  override focus() {
    this._input?.focus();
  }

  private _getNumericAttributeSafe(attr: string) {
    const raw = this.getAttribute(attr);
    return raw && !isNaN(+raw) ? +raw : null;
  }

  private _displayValue() {
    if (this._renderRaw) return this._rawValue;
    const value = this.value.value;
    if (value === undefined || value === null || value === '') return '';
    const pad = this._getNumericAttributeSafe('pad');
    const step = this._getNumericAttributeSafe('step');
    if (this.type === 'number' && pad) return String(value).padStart(pad, '0');
    if (this.type === 'time') return (value as DateTime).toFormat(step && step < 60 ? 'TT' : 'T');
    if (this.type === 'date') return (value as DateTime).toISODate();
    if (this.type === 'datetime-local')
      return (value as DateTime)
        .toLocal()
        .toISO({ suppressMilliseconds: true, suppressSeconds: true, includeOffset: false });
    if (this.type === 'week') return (value as DateTime).toFormat("yyyy-'W'WW");
    if (this.type === 'month') return (value as DateTime).toFormat('yyyy-MM');
    return value;
  }
}
